import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, QueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, CheckCircle2, DollarSign, ArrowLeft, Send, ChevronRight, MessageSquare, Calendar, X, Wrench, Loader2, Download, Clock, Play, Square, Pencil, ClipboardCheck, MapPin } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { AttachmentComposer, PendingTaskAttachment, buildReadyAttachmentUrls } from "@/components/internal-comment-thread";
import { MessageAttachments } from "@/components/message-attachments";
import { useToast } from "@/hooks/use-toast";
import type { TaskMessage, ServiceFormTemplate, ServiceFormSubmission } from "@shared/schema";
import { ServiceFormFill, type ServiceFormFieldError, type ServiceFormSubmitError, type ServiceFormSubmitPayload } from "@/components/service-form-fill";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EnrichedTask, TeamMember, ToastFn, VendorSession, formatCents, formatDate, formatDateTime, padpulseLogo, statusColors } from "./_shared";
import { priorityWithSla } from "@/lib/sla";



export function TaskCard({ task, vendorId, session, onOpen, queryClient, toast, isFirst, onSampleCompleted }: {
  task: EnrichedTask;
  vendorId: number;
  session: VendorSession;
  onOpen: () => void;
  queryClient: QueryClient;
  toast: ToastFn;
  isFirst?: boolean;
  onSampleCompleted?: () => void;
}) {
  const isComplete = ["completed", "settled", "paid", "approved"].includes(task.status);

  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);

  const completeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("PATCH", `/api/vendor-portal/tasks/${task.id}`, { status: "completed" }),
    onSuccess: () => {
      setCompleteConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      toast({ title: "Task marked complete" });
      // Task #1348 — completing a sample task asks the dashboard to open the
      // archive confirmation dialog. Lifted up so the dialog survives this
      // card unmounting when the task moves between Active/Completed tabs.
      if (task.isSample) onSampleCompleted?.();
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Something went wrong."), variant: "destructive" }),
  });

  const statusMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (newStatus: string) => apiRequest("PATCH", `/api/vendor-portal/tasks/${task.id}`, { status: newStatus }),
    onSuccess: (_data, newStatus) => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      // Start Work auto-starts the timer server-side; refresh time-entries so
      // the Time Tracking panel shows the running timer without a second click.
      if (newStatus === "in_progress") {
        queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", task.id, "time-entries"] });
      }
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to update status"), variant: "destructive" }),
  });

  return (
    <>
    {acceptDialogOpen && (
      <AcceptWithEstimateDialog
        open={acceptDialogOpen}
        onOpenChange={setAcceptDialogOpen}
        task={task}
        vendorId={vendorId}
        session={session}
        queryClient={queryClient}
        toast={toast}
      />
    )}
    <AlertDialog
      open={completeConfirmOpen}
      onOpenChange={(open) => { if (!completeMutation.isPending) setCompleteConfirmOpen(open); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark task complete?</AlertDialogTitle>
          <AlertDialogDescription>
            Please confirm that your hours and pricing are finalized as you will not be able to edit it once the task is marked complete.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={completeMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={completeMutation.isPending}
            onClick={() => completeMutation.mutate()}
          >
            {completeMutation.isPending ? "Saving..." : "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`card-vendor-task-${task.id}`}
      data-tour={isFirst ? "tasks-task-card" : undefined}
      onClick={onOpen}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {task.type === "maintenance" && (
                <Badge
                  variant="secondary"
                  className="bg-purple-100 text-purple-800 hover:bg-purple-100 flex-shrink-0"
                  data-testid={`badge-maintenance-task-${task.id}`}
                >
                  <Wrench className="h-3 w-3 mr-1" />
                  Maintenance
                </Badge>
              )}
              {task.formTemplateId != null && (
                <Badge
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 flex-shrink-0"
                  data-testid={`badge-form-task-${task.id}`}
                >
                  <ClipboardCheck className="h-3 w-3 mr-1" />
                  {(task.formSubmissionCount ?? 0) > 0 ? "Form submitted" : "Form"}
                </Badge>
              )}
              <p className="font-medium text-gray-900 truncate">{task.title}</p>
              {task.isSample && (
                <Badge
                  variant="secondary"
                  className="bg-purple-100 text-purple-800 hover:bg-purple-100 flex-shrink-0"
                  data-testid={`badge-sample-task-${task.id}`}
                >
                  Sample
                </Badge>
              )}
            </div>
            {task.isSample ? (
              <p className="text-xs text-gray-500" data-testid={`text-example-property-${task.id}`}>
                Example Property · 123 Demo Lane
              </p>
            ) : (
              task.propertyId && (
                <p className="text-xs text-gray-500" data-testid={`text-task-property-${task.id}`}>
                  {task.propertyAddress || task.propertyName || `Property #${task.propertyId}`}
                </p>
              )
            )}
            <p className="text-xs text-gray-500 mt-0.5" data-testid={`text-assignee-${task.id}`}>
              Assigned to: {task.assignedTeamMemberName || "Owner"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-testid={`text-task-priority-${task.id}`}>
              Priority: {priorityWithSla(task.slaConfig, task.priority)}
            </p>
            {task.scheduledStart && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(task.scheduledStart)}
              </p>
            )}
          </div>
          <div className="text-right ml-3 flex-shrink-0">
            <Badge className={statusColors[task.status] || "bg-gray-100 text-gray-800"}>
              {task.status.replace("_", " ")}
            </Badge>
            {task.priceCents && <p className="text-sm font-medium mt-1">{formatCents(task.priceCents)}</p>}
          </div>
        </div>
        {!isComplete && (
          <div className="flex items-center gap-2 mt-3">
            {task.status === "accepted" && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                data-testid={`button-start-work-${task.id}`}
                disabled={statusMutation.isPending}
                onClick={(e) => { e.stopPropagation(); statusMutation.mutate("in_progress"); }}
              >
                <Play className="h-3.5 w-3.5 mr-1" />
                {statusMutation.isPending ? "Starting..." : "Start Work"}
              </Button>
            )}
            {["in_progress", "accepted"].includes(task.status) && (
              <Button
                size="sm"
                className="flex-1"
                data-testid={`button-complete-task-${task.id}`}
                disabled={completeMutation.isPending}
                onClick={(e) => { e.stopPropagation(); setCompleteConfirmOpen(true); }}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Mark Complete
              </Button>
            )}
            {task.status === "pending" && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                data-testid={`button-accept-task-${task.id}`}
                onClick={(e) => { e.stopPropagation(); setAcceptDialogOpen(true); }}
              >
                Accept Task
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="flex-shrink-0"
              data-testid={`button-view-task-${task.id}`}
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
}


// ── Archive Sample Task Dialog ────────────────────────────────────────────────
// Task #1348 — shown after the vendor marks a sample task complete. Confirming
// archives every sample task belonging to the vendor so the dashboard returns
// to a clean state and the demo content never reappears on refresh.
export function ArchiveSampleTaskDialog({ open, onOpenChange, vendorId, queryClient, toast }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: number;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const archiveMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/vendor-portal/tasks/archive-samples", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      toast({ title: "Sample tasks archived" });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to archive sample tasks."), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-archive-sample-task">
        <DialogHeader>
          <DialogTitle>Archive sample task?</DialogTitle>
          <DialogDescription>
            The two sample tasks were here to show you what a real assignment looks like. Archive them now to clear the demo content from your dashboard — they will not come back.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={archiveMutation.isPending}
            data-testid="button-keep-sample-tasks"
          >
            Keep them
          </Button>
          <Button
            onClick={() => archiveMutation.mutate()}
            disabled={archiveMutation.isPending}
            data-testid="button-archive-sample-tasks"
          >
            {archiveMutation.isPending ? "Archiving..." : "Archive"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ── Accept With Estimate Dialog ────────────────────────────────────────────────
// Task #2187 — Shown when a vendor accepts a task. Replaces the one-click
// accept with a form that captures rate type, rate, estimate, travel fee, note.
export function AcceptWithEstimateDialog({ open, onOpenChange, task, vendorId, session, queryClient, toast, onAccepted }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: EnrichedTask;
  vendorId: number;
  session: VendorSession;
  queryClient: QueryClient;
  toast: ToastFn;
  onAccepted?: () => void;
}) {
  const vendor = session as VendorSession & { defaultRateCents?: number | null; rateType?: string | null; defaultTravelFeeCents?: number | null };
  // Task #2783 — prefer the assigned team member's saved rates, falling back to
  // the vendor-level defaults when the member has none set.
  const memberHourlyRateCents = task.assignedMemberHourlyRateCents ?? null;
  const memberTravelFeeCents = task.assignedMemberTravelFeeCents ?? null;
  const memberFlatOfferings = task.assignedMemberFlatRateOfferings ?? [];
  const initialRateCents = memberHourlyRateCents ?? vendor.defaultRateCents ?? null;
  const initialTravelFeeCents = memberTravelFeeCents ?? vendor.defaultTravelFeeCents ?? null;
  const [rateType, setRateType] = useState<"hourly" | "fixed">((vendor.rateType as "hourly" | "fixed") || "hourly");
  const [rateInput, setRateInput] = useState<string>(initialRateCents != null ? String(initialRateCents / 100) : "");
  const [estimateHoursInput, setEstimateHoursInput] = useState<string>("");
  const [travelFeeInput, setTravelFeeInput] = useState<string>(initialTravelFeeCents != null ? String(initialTravelFeeCents / 100) : "");
  const [note, setNote] = useState<string>("");

  const estimateCents = rateType === "hourly" && rateInput && estimateHoursInput
    ? Math.round(parseFloat(rateInput) * parseFloat(estimateHoursInput) * 100)
    : rateType === "fixed" && rateInput
    ? Math.round(parseFloat(rateInput) * 100)
    : null;
  const travelFeeCents = travelFeeInput ? Math.round(parseFloat(travelFeeInput) * 100) : 0;
  const totalCents = (estimateCents ?? 0) + travelFeeCents;

  const acceptMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => {
      const rateCents = Math.round(parseFloat(rateInput) * 100);
      return apiRequest("POST", `/api/vendor-portal/tasks/${task.id}/accept`, {
        lockedRateType: rateType,
        lockedRateCents: rateCents,
        estimateHours: rateType === "hourly" && estimateHoursInput ? parseFloat(estimateHoursInput) : null,
        estimateCents,
        travelFeeCents: travelFeeCents || null,
        vendorEstimateNote: note.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", task.id, "status-history"] });
      toast({ title: "Task accepted", description: "The host has been notified." });
      onOpenChange(false);
      onAccepted?.();
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to accept task"), variant: "destructive" }),
  });

  const missingHourlyEstimate = rateType === "hourly" && (!estimateHoursInput || parseFloat(estimateHoursInput) <= 0);
  const canSubmit = rateInput && parseFloat(rateInput) > 0 && !missingHourlyEstimate && !acceptMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90dvh]" data-testid="dialog-accept-with-estimate">
        <DialogHeader>
          <DialogTitle>Accept Job with Estimate</DialogTitle>
          <DialogDescription>Set your rate and provide an estimate before accepting "{task.title}".</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 overflow-y-auto flex-1">
          <div>
            <Label className="text-sm font-medium">Rate Type</Label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={rateType === "hourly" ? "default" : "outline"}
                onClick={() => setRateType("hourly")}
                data-testid="button-rate-type-hourly"
              >
                <Clock className="h-3.5 w-3.5 mr-1" />Hourly
              </Button>
              <Button
                type="button"
                size="sm"
                variant={rateType === "fixed" ? "default" : "outline"}
                onClick={() => setRateType("fixed")}
                data-testid="button-rate-type-fixed"
              >
                <DollarSign className="h-3.5 w-3.5 mr-1" />Fixed / Flat
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="input-rate" className="text-sm font-medium">
              {rateType === "hourly" ? "Hourly Rate ($)" : "Flat Rate ($)"}
            </Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id="input-rate"
                type="number"
                min="0"
                step="0.01"
                className="pl-6"
                placeholder="0.00"
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                data-testid="input-rate"
              />
            </div>
            {/* Task #2783 — assigned member's flat-rate catalog as clickable options */}
            {rateType === "fixed" && memberFlatOfferings.length > 0 && (
              <div className="mt-2" data-testid="section-flat-rate-offerings">
                <p className="text-xs text-gray-500 mb-1">Quick-fill from saved services:</p>
                <div className="flex flex-wrap gap-1.5">
                  {memberFlatOfferings.map((offering, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setRateInput(String(offering.priceCents / 100))}
                      data-testid={`button-flat-offering-${idx}`}
                    >
                      {offering.name} · ${(offering.priceCents / 100).toFixed(2)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {rateType === "hourly" && (
            <div>
              <Label htmlFor="input-estimate-hours" className="text-sm font-medium">Estimated Hours (optional)</Label>
              <Input
                id="input-estimate-hours"
                type="number"
                min="0"
                step="0.5"
                className="mt-1"
                placeholder="e.g. 2.5"
                value={estimateHoursInput}
                onChange={e => setEstimateHoursInput(e.target.value)}
                data-testid="input-estimate-hours"
              />
              {missingHourlyEstimate && estimateHoursInput !== "" && (
                <p className="text-xs text-amber-600 mt-1" data-testid="text-estimate-hours-warning">
                  An estimate helps the host plan costs. Without it, the final price will be based solely on your tracked time.
                </p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="input-travel-fee" className="text-sm font-medium">Travel Fee ($, optional)</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id="input-travel-fee"
                type="number"
                min="0"
                step="0.01"
                className="pl-6"
                placeholder="0.00"
                value={travelFeeInput}
                onChange={e => setTravelFeeInput(e.target.value)}
                data-testid="input-travel-fee"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="textarea-estimate-note" className="text-sm font-medium">Note for host (optional)</Label>
            <Textarea
              id="textarea-estimate-note"
              className="mt-1"
              placeholder="e.g. Includes parts and labor. Will confirm exact time on arrival."
              rows={2}
              maxLength={2000}
              value={note}
              onChange={e => setNote(e.target.value)}
              data-testid="textarea-estimate-note"
            />
          </div>

          {(estimateCents !== null || travelFeeCents > 0) && (
            <div className="bg-blue-50 dark:bg-blue-950/40 rounded-md p-3 text-sm space-y-1" data-testid="section-estimate-summary">
              {estimateCents !== null && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{rateType === "hourly" ? "Labor estimate" : "Flat rate"}</span>
                  <span className="font-medium">${(estimateCents / 100).toFixed(2)}</span>
                </div>
              )}
              {travelFeeCents > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Travel fee</span>
                  <span className="font-medium">${(travelFeeCents / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total estimate</span>
                <span>${(totalCents / 100).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-accept-estimate">Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() => acceptMutation.mutate()}
            data-testid="button-confirm-accept-estimate"
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept Job"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Task #4315 — Same estimate form as AcceptWithEstimateDialog but POSTs to
// /submit-quote instead of /accept. The task stays open/sent_to_vendor and an
// owner approval row is created; the vendor sees the pending-approval banner.
export function SubmitQuoteDialog({ open, onOpenChange, task, vendorId, session, queryClient, toast }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: EnrichedTask;
  vendorId: number;
  session: VendorSession;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const vendor = session as VendorSession & { defaultRateCents?: number | null; rateType?: string | null; defaultTravelFeeCents?: number | null };
  const memberHourlyRateCents = task.assignedMemberHourlyRateCents ?? null;
  const memberTravelFeeCents = task.assignedMemberTravelFeeCents ?? null;
  const memberFlatOfferings = task.assignedMemberFlatRateOfferings ?? [];
  const initialRateCents = memberHourlyRateCents ?? vendor.defaultRateCents ?? null;
  const initialTravelFeeCents = memberTravelFeeCents ?? vendor.defaultTravelFeeCents ?? null;
  const [rateType, setRateType] = useState<"hourly" | "fixed">((vendor.rateType as "hourly" | "fixed") || "hourly");
  const [rateInput, setRateInput] = useState<string>(initialRateCents != null ? String(initialRateCents / 100) : "");
  const [estimateHoursInput, setEstimateHoursInput] = useState<string>("");
  const [travelFeeInput, setTravelFeeInput] = useState<string>(initialTravelFeeCents != null ? String(initialTravelFeeCents / 100) : "");
  const [note, setNote] = useState<string>("");

  const estimateCents = rateType === "hourly" && rateInput && estimateHoursInput
    ? Math.round(parseFloat(rateInput) * parseFloat(estimateHoursInput) * 100)
    : rateType === "fixed" && rateInput
    ? Math.round(parseFloat(rateInput) * 100)
    : null;
  const travelFeeCents = travelFeeInput ? Math.round(parseFloat(travelFeeInput) * 100) : 0;
  const totalCents = (estimateCents ?? 0) + travelFeeCents;

  const submitQuoteMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => {
      const rateCents = Math.round(parseFloat(rateInput) * 100);
      return apiRequest("POST", `/api/vendor-portal/tasks/${task.id}/submit-quote`, {
        lockedRateType: rateType,
        lockedRateCents: rateCents,
        estimateHours: rateType === "hourly" && estimateHoursInput ? parseFloat(estimateHoursInput) : null,
        estimateCents,
        travelFeeCents: travelFeeCents || null,
        vendorEstimateNote: note.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      toast({ title: "Quote submitted", description: "The property owner has been notified and must approve before work begins." });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to submit quote"), variant: "destructive" }),
  });

  const missingHourlyEstimate = rateType === "hourly" && (!estimateHoursInput || parseFloat(estimateHoursInput) <= 0);
  const canSubmit = rateInput && parseFloat(rateInput) > 0 && !missingHourlyEstimate && !submitQuoteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitQuoteMutation.isPending) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90dvh]" data-testid="dialog-submit-quote">
        <DialogHeader>
          <DialogTitle>Submit Quote for Owner Approval</DialogTitle>
          <DialogDescription>
            Set your rate and estimate. The property owner will be asked to approve the cost before work begins on "{task.title}".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 overflow-y-auto flex-1">
          <div>
            <Label className="text-sm font-medium">Rate Type</Label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={rateType === "hourly" ? "default" : "outline"}
                onClick={() => setRateType("hourly")}
                data-testid="button-sq-rate-type-hourly"
              >
                <Clock className="h-3.5 w-3.5 mr-1" />Hourly
              </Button>
              <Button
                type="button"
                size="sm"
                variant={rateType === "fixed" ? "default" : "outline"}
                onClick={() => setRateType("fixed")}
                data-testid="button-sq-rate-type-fixed"
              >
                <DollarSign className="h-3.5 w-3.5 mr-1" />Fixed / Flat
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="input-sq-rate" className="text-sm font-medium">
              {rateType === "hourly" ? "Hourly Rate ($)" : "Flat Rate ($)"}
            </Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id="input-sq-rate"
                type="number"
                min="0"
                step="0.01"
                className="pl-6"
                placeholder="0.00"
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                data-testid="input-sq-rate"
              />
            </div>
            {rateType === "fixed" && memberFlatOfferings.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Quick-fill from saved services:</p>
                <div className="flex flex-wrap gap-1.5">
                  {memberFlatOfferings.map((offering, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setRateInput(String(offering.priceCents / 100))}
                      data-testid={`button-sq-flat-offering-${idx}`}
                    >
                      {offering.name} · ${(offering.priceCents / 100).toFixed(2)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {rateType === "hourly" && (
            <div>
              <Label htmlFor="input-sq-estimate-hours" className="text-sm font-medium">Estimated Hours</Label>
              <Input
                id="input-sq-estimate-hours"
                type="number"
                min="0"
                step="0.5"
                className="mt-1"
                placeholder="e.g. 2.5"
                value={estimateHoursInput}
                onChange={e => setEstimateHoursInput(e.target.value)}
                data-testid="input-sq-estimate-hours"
              />
              {missingHourlyEstimate && estimateHoursInput !== "" && (
                <p className="text-xs text-amber-600 mt-1">Enter a positive number of hours for an hourly quote.</p>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="input-sq-travel-fee" className="text-sm font-medium">Travel Fee ($, optional)</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id="input-sq-travel-fee"
                type="number"
                min="0"
                step="0.01"
                className="pl-6"
                placeholder="0.00"
                value={travelFeeInput}
                onChange={e => setTravelFeeInput(e.target.value)}
                data-testid="input-sq-travel-fee"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="textarea-sq-note" className="text-sm font-medium">Note for owner (optional)</Label>
            <Textarea
              id="textarea-sq-note"
              className="mt-1"
              placeholder="e.g. Includes parts and labor. Scope may change after on-site inspection."
              rows={2}
              maxLength={2000}
              value={note}
              onChange={e => setNote(e.target.value)}
              data-testid="textarea-sq-note"
            />
          </div>
          {(estimateCents !== null || travelFeeCents > 0) && (
            <div className="bg-blue-50 dark:bg-blue-950/40 rounded-md p-3 text-sm space-y-1" data-testid="section-sq-estimate-summary">
              {estimateCents !== null && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{rateType === "hourly" ? "Labor estimate" : "Flat rate"}</span>
                  <span className="font-medium">${(estimateCents / 100).toFixed(2)}</span>
                </div>
              )}
              {travelFeeCents > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Travel fee</span>
                  <span className="font-medium">${(travelFeeCents / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total estimate</span>
                <span>${(totalCents / 100).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitQuoteMutation.isPending} data-testid="button-cancel-submit-quote">Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() => submitQuoteMutation.mutate()}
            data-testid="button-confirm-submit-quote"
          >
            {submitQuoteMutation.isPending ? "Submitting..." : "Submit Quote for Approval"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Timer Section ──────────────────────────────────────────────────────────────
// Task #2187 — Shows a live timer for accepted tasks in progress, plus a list
// of recorded time entries. Supports start/pause, manual add, and delete.
export interface TimeEntry {
  id: number;
  taskId: number;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  isManual: boolean;
  note: string | null;
  vendorTeamMemberId: number | null;
}


export function TimerSection({ task, vendorId, session, queryClient, toast, canWrite }: {
  task: EnrichedTask;
  vendorId: number;
  session: VendorSession;
  queryClient: QueryClient;
  toast: ToastFn;
  canWrite: boolean;
}) {
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manualStartTime, setManualStartTime] = useState("09:00");
  const [manualEndTime, setManualEndTime] = useState("10:00");
  const [manualNote, setManualNote] = useState("");
  const isHourly = task.lockedRateType === "hourly";
  // Edit state for existing entries
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editNote, setEditNote] = useState("");
  // Delete-with-reason state
  const [deletingEntryId, setDeletingEntryId] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  const { data: entries = [], isLoading: entriesLoading } = useQuery<TimeEntry[]>({
    queryKey: ["vendor", vendorId, "tasks", task.id, "time-entries"],
    queryFn: async () => {
      const r = await fetch(`/api/vendor-portal/tasks/${task.id}/time-entries`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || "Failed");
      return r.json();
    },
    refetchInterval: 10000,
  });

  const openEntry = entries.find(e => !e.endedAt);

  useEffect(() => {
    if (openEntry) {
      const start = new Date(openEntry.startedAt).getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [openEntry]);

  const invalidateEntries = () =>
    queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", task.id, "time-entries"] });

  const startMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", `/api/vendor-portal/tasks/${task.id}/timer/start`, {}),
    onSuccess: invalidateEntries,
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to start timer"), variant: "destructive" }),
  });

  const stopMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", `/api/vendor-portal/tasks/${task.id}/timer/stop`, {}),
    onSuccess: invalidateEntries,
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to pause timer"), variant: "destructive" }),
  });

  const deleteEntryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: ({ entryId, reason }: { entryId: number; reason?: string }) =>
      apiRequest("DELETE", `/api/vendor-portal/tasks/${task.id}/time-entries/${entryId}`, reason ? { reason } : {}),
    onSuccess: () => {
      invalidateEntries();
      setDeletingEntryId(null);
      setDeleteReason("");
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to delete entry"), variant: "destructive" }),
  });

  const editEntryMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: ({ entryId, startedAt, endedAt, note }: { entryId: number; startedAt: string; endedAt: string; note?: string }) => {
      if (new Date(endedAt) <= new Date(startedAt)) throw new Error("End time must be after start time");
      return apiRequest("PATCH", `/api/vendor-portal/tasks/${task.id}/time-entries/${entryId}`, { startedAt, endedAt, note });
    },
    onSuccess: () => {
      invalidateEntries();
      setEditingEntryId(null);
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to update entry"), variant: "destructive" }),
  });

  const addManualMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => {
      const startedAt = new Date(`${manualDate}T${manualStartTime}:00`).toISOString();
      const endedAt = new Date(`${manualDate}T${manualEndTime}:00`).toISOString();
      if (new Date(endedAt) <= new Date(startedAt)) throw new Error("End time must be after start time");
      return apiRequest("POST", `/api/vendor-portal/tasks/${task.id}/time-entries`, {
        startedAt, endedAt, note: manualNote || undefined, isManual: true,
      });
    },
    onSuccess: () => {
      invalidateEntries();
      setShowManualForm(false);
      setManualNote("");
      toast({ title: "Time entry added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to add entry"), variant: "destructive" }),
  });

  const formatElapsed = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h > 0 ? `${h}h ` : ""}${m}m ${sec}s`;
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const totalLogged = entries.reduce((sum, e) => {
    if (e.durationSeconds) return sum + e.durationSeconds;
    if (e.endedAt) return sum + Math.max(0, Math.floor((new Date(e.endedAt).getTime() - new Date(e.startedAt).getTime()) / 1000));
    return sum + elapsed;
  }, 0);

  return (
    <Card data-testid="card-timer-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1">
          <Clock className="h-4 w-4" />
          {isHourly ? "Time Tracking" : "Time Tracking (internal)"}
          {totalLogged > 0 && (
            <Badge variant="secondary" className="ml-auto" data-testid="badge-total-time">
              {formatDuration(totalLogged)} total
            </Badge>
          )}
        </CardTitle>
        {!isHourly && (
          <p className="text-xs text-gray-400 mt-0.5">Flat-rate job — time is tracked for your records only and won't change the price.</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {canWrite && (
          <div className="flex items-center gap-3">
            {openEntry ? (
              <>
                <div className="flex-1 font-mono text-lg font-semibold text-blue-700" data-testid="text-timer-elapsed">
                  {formatElapsed(elapsed)}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                  disabled={stopMutation.isPending}
                  onClick={() => stopMutation.mutate()}
                  data-testid="button-timer-stop"
                >
                  <Square className="h-3.5 w-3.5 mr-1" />
                  {stopMutation.isPending ? "Pausing..." : "Pause"}
                </Button>
              </>
            ) : (
              <>
                <p className="flex-1 text-sm text-gray-500">{openEntry ? "" : "Start the timer when you begin work."}</p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={startMutation.isPending}
                  onClick={() => startMutation.mutate()}
                  data-testid="button-timer-start"
                >
                  <Play className="h-3.5 w-3.5 mr-1" />
                  {startMutation.isPending ? "Starting..." : entries.length > 0 ? "Resume" : "Start Timer"}
                </Button>
              </>
            )}
          </div>
        )}

        {canWrite && !openEntry && (
          <div>
            {showManualForm ? (
              <div className="border rounded-md p-3 space-y-2 bg-gray-50" data-testid="form-manual-time-entry">
                <p className="text-xs font-medium text-gray-700">Add manual entry</p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Date</Label>
                    <Input
                      type="date"
                      value={manualDate}
                      onChange={e => setManualDate(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="input-manual-date"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Start time</Label>
                    <Input
                      type="time"
                      value={manualStartTime}
                      onChange={e => setManualStartTime(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="input-manual-start-time"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">End time</Label>
                    <Input
                      type="time"
                      value={manualEndTime}
                      onChange={e => setManualEndTime(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="input-manual-end-time"
                    />
                  </div>
                </div>
                <Input
                  type="text"
                  placeholder="Note (optional)"
                  value={manualNote}
                  onChange={e => setManualNote(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="input-manual-note"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    disabled={addManualMutation.isPending}
                    onClick={() => addManualMutation.mutate()}
                    data-testid="button-manual-entry-save"
                  >
                    {addManualMutation.isPending ? "Saving..." : "Save Entry"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowManualForm(false)}
                    data-testid="button-manual-entry-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="w-full text-xs text-gray-400 hover:text-gray-600"
                onClick={() => setShowManualForm(true)}
                data-testid="button-add-manual-time-entry"
              >
                + Add time manually
              </Button>
            )}
          </div>
        )}

        {/* Delete-with-reason dialog */}
        {deletingEntryId !== null && (
          <div className="border rounded-md p-3 bg-red-50 space-y-2" data-testid="form-delete-reason">
            <p className="text-xs font-medium text-red-700">Why are you removing this entry?</p>
            <Input
              type="text"
              placeholder="Reason (optional)"
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              className="h-8 text-sm"
              data-testid="input-delete-reason"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                disabled={deleteEntryMutation.isPending}
                onClick={() => deleteEntryMutation.mutate({ entryId: deletingEntryId, reason: deleteReason || undefined })}
                data-testid="button-confirm-delete-entry"
              >
                {deleteEntryMutation.isPending ? "Removing..." : "Remove Entry"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setDeletingEntryId(null); setDeleteReason(""); }} data-testid="button-cancel-delete-entry">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {entriesLoading ? (
          <p className="text-xs text-gray-400 text-center py-2">Loading time entries…</p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-2">No time logged yet.</p>
        ) : (
          <div className="space-y-2">
            {entries.map(entry => {
              const durSecs = entry.durationSeconds
                ?? (entry.endedAt ? Math.max(0, Math.floor((new Date(entry.endedAt).getTime() - new Date(entry.startedAt).getTime()) / 1000)) : elapsed);
              const isEditing = editingEntryId === entry.id;
              return (
                <div key={entry.id} className="text-sm" data-testid={`row-time-entry-${entry.id}`}>
                  {isEditing ? (
                    <div className="border rounded-md p-2 space-y-1.5 bg-blue-50 dark:bg-blue-950/40" data-testid={`form-edit-entry-${entry.id}`}>
                      <p className="text-xs font-medium text-gray-600">Edit time entry</p>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Date</Label>
                          <Input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="h-7 text-xs" data-testid="input-edit-date" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Start</Label>
                          <Input type="time" value={editStartTime} onChange={e => setEditStartTime(e.target.value)} className="h-7 text-xs" data-testid="input-edit-start" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">End</Label>
                          <Input type="time" value={editEndTime} onChange={e => setEditEndTime(e.target.value)} className="h-7 text-xs" data-testid="input-edit-end" />
                        </div>
                      </div>
                      <Input type="text" placeholder="Note" value={editNote} onChange={e => setEditNote(e.target.value)} className="h-7 text-xs" data-testid="input-edit-note" />
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 h-7 text-xs" disabled={editEntryMutation.isPending}
                          onClick={() => editEntryMutation.mutate({
                            entryId: entry.id,
                            startedAt: new Date(`${editDate}T${editStartTime}:00`).toISOString(),
                            endedAt: new Date(`${editDate}T${editEndTime}:00`).toISOString(),
                            note: editNote || undefined,
                          })}
                          data-testid={`button-save-edit-entry-${entry.id}`}
                        >
                          {editEntryMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingEntryId(null)} data-testid={`button-cancel-edit-entry-${entry.id}`}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium">{formatDuration(durSecs)}</span>
                          {!entry.endedAt && <Badge variant="secondary" className="ml-1 text-xs">Running</Badge>}
                          {entry.isManual && <Badge variant="outline" className="ml-1 text-xs">Manual</Badge>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(entry.startedAt).toLocaleDateString()}{" "}
                          {new Date(entry.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {entry.endedAt && (
                            <> – {new Date(entry.endedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
                          )}
                        </p>
                        {entry.note && <p className="text-xs text-gray-500 mt-0.5">{entry.note}</p>}
                      </div>
                      {canWrite && !openEntry && entry.endedAt && (
                        <Button
                          variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400 hover:text-blue-600"
                          onClick={() => {
                            const d = new Date(entry.startedAt);
                            const dateStr = d.toISOString().slice(0, 10);
                            const startHH = d.toTimeString().slice(0, 5);
                            const endD = new Date(entry.endedAt!);
                            const endHH = endD.toTimeString().slice(0, 5);
                            setEditDate(dateStr);
                            setEditStartTime(startHH);
                            setEditEndTime(endHH);
                            setEditNote(entry.note || "");
                            setEditingEntryId(entry.id);
                          }}
                          data-testid={`button-edit-time-entry-${entry.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      {canWrite && !openEntry && (
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                          onClick={() => { setDeletingEntryId(entry.id); setDeleteReason(""); }}
                          disabled={deleteEntryMutation.isPending}
                          data-testid={`button-delete-time-entry-${entry.id}`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isHourly && task.lockedRateCents && totalLogged > 0 && (
          <div className="bg-gray-50 rounded-md p-2 text-xs text-gray-600" data-testid="section-running-total">
            Running total: <span className="font-semibold">${((totalLogged / 3600) * task.lockedRateCents / 100).toFixed(2)}</span>
            {" "}@ ${(task.lockedRateCents / 100).toFixed(2)}/hr
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ── Task Detail View ───────────────────────────────────────────────────────────

// Forms-on-tasks — renders the form-task's fillable form (shared
// <ServiceFormFill>, same component as the public magic-link page) until a
// submission exists, then a submission summary. Data comes from
// GET /api/vendor-portal/tasks/:id/form; uploads + submit hit the task-scoped
// endpoints so the server can verify intents against the vendor session.
export function TaskFormSection({ task, vendorId, queryClient, canWrite }: {
  task: EnrichedTask;
  vendorId: number;
  queryClient: QueryClient;
  canWrite: boolean;
}) {
  const formQuery = useQuery<{ template: ServiceFormTemplate; submissions: ServiceFormSubmission[] }>({
    queryKey: ["vendor", vendorId, "tasks", task.id, "form"],
    queryFn: async () => {
      const r = await fetch(`/api/vendor-portal/tasks/${task.id}/form`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const getUploadUrl = async (): Promise<{ uploadUrl: string; objectPath: string }> => {
    const r = await fetch(`/api/vendor-portal/tasks/${task.id}/form-upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: "{}",
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error((err as { message?: string })?.message || `Upload URL failed (${r.status})`);
    }
    return r.json();
  };

  const submitForm = async (payload: ServiceFormSubmitPayload) => {
    const r = await fetch(`/api/vendor-portal/tasks/${task.id}/form-submission`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { message?: string; errors?: ServiceFormFieldError[] };
      const submitError = new Error(err.message || "Submission failed") as ServiceFormSubmitError;
      submitError.fieldErrors = err.errors;
      throw submitError;
    }
    return r.json();
  };

  if (formQuery.isLoading) {
    return (
      <Card data-testid="section-task-form">
        <CardContent className="p-4 text-sm text-gray-400">Loading form…</CardContent>
      </Card>
    );
  }
  if (formQuery.isError || !formQuery.data) {
    return (
      <Card data-testid="section-task-form">
        <CardContent className="p-4 text-sm text-red-500">
          {(formQuery.error as Error)?.message || "Failed to load the form."}
        </CardContent>
      </Card>
    );
  }

  const { template, submissions } = formQuery.data;
  const submission = submissions[0];

  if (submission) {
    const responses = (submission.checklistResponses || {}) as Record<string, boolean>;
    const checkedCount = Object.values(responses).filter(Boolean).length;
    const photoCount = Object.values((submission.photoUrls || {}) as Record<string, string[]>)
      .reduce((n, urls) => n + (Array.isArray(urls) ? urls.length : 0), 0);
    return (
      <Card data-testid="section-task-form-submitted">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <h3 className="font-medium text-sm text-gray-900">{template.name} — submitted</h3>
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <p data-testid="text-form-submitted-at">
              Submitted {formatDateTime(submission.submittedAt)}
              {submission.submitterName ? ` by ${submission.submitterName}` : ""}
            </p>
            <p>{checkedCount} checklist item{checkedCount === 1 ? "" : "s"} checked · {photoCount} photo{photoCount === 1 ? "" : "s"}</p>
            {submission.locationFlagged ? (
              <p className="text-amber-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Location flagged{submission.locationDistanceMiles != null ? ` (${submission.locationDistanceMiles.toFixed(1)} mi from property)` : ""}
              </p>
            ) : submission.latitude != null ? (
              <p className="text-green-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Location verified on-site
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canWrite) {
    return (
      <Card data-testid="section-task-form">
        <CardContent className="p-4 text-sm text-gray-500">
          This task includes the "{template.name}" form. It hasn't been submitted yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="section-task-form">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className="h-4 w-4 text-brand" />
        <h3 className="font-medium text-sm text-gray-900">{template.name}</h3>
      </div>
      {template.description && (
        <p className="text-xs text-gray-500 mb-3">{template.description}</p>
      )}
      <ServiceFormFill
        template={template}
        embedded
        getUploadUrl={getUploadUrl}
        submit={submitForm}
        onSubmitted={() => {
          queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", task.id, "form"] });
          queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
        }}
        testIdPrefix="task-form-"
      />
    </div>
  );
}

export function TaskDetailView({ taskId, task, vendorId, session, onBack, onLogout, queryClient, showAll, onSampleCompleted }: {
  taskId: number;
  task: EnrichedTask | undefined;
  vendorId: number;
  session: VendorSession;
  onBack: () => void;
  onLogout: () => void;
  queryClient: QueryClient;
  showAll?: boolean;
  onSampleCompleted?: () => void;
}) {
  const [messageText, setMessageText] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<PendingTaskAttachment[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const isOwner = session.isOwner || false;
  const [reassignMemberId, setReassignMemberId] = useState<string>(task?.assignedTeamMemberId ? String(task.assignedTeamMemberId) : "none");

  useEffect(() => {
    setReassignMemberId(task?.assignedTeamMemberId ? String(task.assignedTeamMemberId) : "none");
  }, [task?.assignedTeamMemberId]);

  // Assigned non-owner team members can always write on their own task, regardless
  // of showAll mode. Owners retain full write access. In showAll (all-team browse)
  // mode, non-assigned members are read-only — consistent with canSessionWriteTask.
  const isCurrentMemberAssigned = task?.assignedTeamMemberId === (session.vendorTeamMemberId ?? null);
  const canWrite = isOwner || isCurrentMemberAssigned;

  const messagesUrl = showAll
    ? `/api/vendor-portal/tasks/${taskId}/messages?showAll=true`
    : `/api/vendor-portal/tasks/${taskId}/messages`;

  const { data: messages = [], isLoading: messagesLoading } = useQuery<TaskMessage[]>({
    queryKey: ["vendor", vendorId, "tasks", taskId, "messages", showAll],
    queryFn: async () => {
      const r = await fetch(messagesUrl, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
    refetchInterval: 10000,
  });

  const { data: teamMembers = [] } = useQuery<TeamMember[]>({
    queryKey: ["vendor", vendorId, "team"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/team", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
    enabled: isOwner,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", `/api/vendor-portal/tasks/${taskId}/messages`, { body: messageText, attachments: buildReadyAttachmentUrls(pendingAttachments) }),
    onSuccess: () => {
      setMessageText("");
      setPendingAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", taskId, "messages"] });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to send message."), variant: "destructive" }),
  });

  type StatusHistoryEntry = {
    fromStatus: string | null;
    toStatus: string;
    changedByName: string | null;
    changedAt: string;
    note: string | null;
    actorType: "host" | "vendor" | "system";
  };

  const statusHistoryUrl = showAll
    ? `/api/vendor-portal/tasks/${taskId}/status-history?showAll=true`
    : `/api/vendor-portal/tasks/${taskId}/status-history`;

  const { data: statusHistory = [], isLoading: statusHistoryLoading } = useQuery<StatusHistoryEntry[]>({
    queryKey: ["vendor", vendorId, "tasks", taskId, "status-history", showAll],
    queryFn: async () => {
      const r = await fetch(statusHistoryUrl, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);

  const completeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("PATCH", `/api/vendor-portal/tasks/${taskId}`, { status: "completed" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", taskId, "status-history"] });
      toast({ title: "Task marked complete" });
      // Task #1348 — completing the pending sample asks the dashboard to
      // surface the archive confirmation dialog.
      if (task?.isSample) onSampleCompleted?.();
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Something went wrong."), variant: "destructive" }),
  });

  // Task #1968 — maintenance tasks support on_hold + decline (open) status
  // transitions in addition to the standard pending → in_progress → completed
  // operations flow. Vendors hit the unified PATCH route for both.
  const [declineOpen, setDeclineOpen] = useState(false);
  // Task #4315 — new "decline with reason" flow for open/sent_to_vendor jobs
  const [declineWithReasonOpen, setDeclineWithReasonOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  // Task #4315 — "Submit Quote for owner approval" flow
  const [submitQuoteOpen, setSubmitQuoteOpen] = useState(false);

  const declineWithReasonMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", `/api/vendor-portal/tasks/${taskId}/decline`, { reason: declineReason.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", taskId, "messages"] });
      setDeclineWithReasonOpen(false);
      setDeclineReason("");
      toast({ title: "Job declined", description: "Your reason has been sent to the host." });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to decline job."), variant: "destructive" }),
  });

  const statusMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (status: string) =>
      apiRequest("PATCH", `/api/vendor-portal/tasks/${taskId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", taskId, "status-history"] });
      toast({ title: "Status updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to update status."), variant: "destructive" }),
  });

  const reassignMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("PATCH", `/api/vendor-portal/tasks/${taskId}`, { assignedTeamMemberId: (reassignMemberId && reassignMemberId !== "none") ? parseInt(reassignMemberId, 10) : null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "tasks", taskId, "messages"] });
      toast({ title: "Task reassigned" });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to reassign task."), variant: "destructive" }),
  });

  const activeMembers = teamMembers.filter(m => m.status === "active");
  const isComplete = task ? ["completed", "settled", "paid", "approved"].includes(task.status) : false;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="page-task-detail">
      <header className="bg-white border-b px-4 py-2 shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-2 relative">
          <button
            className="flex items-center gap-1.5 text-sm min-h-[44px] px-1 rounded text-brand hover:bg-brand-muted transition-colors font-medium"
            onClick={onBack}
            data-testid="button-back-to-tasks"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span>Tasks</span>
          </button>
          <div className="flex items-center gap-1.5 absolute left-1/2 -translate-x-1/2">
            <img src={padpulseLogo} alt="PadPulse" className="h-6 w-auto" />
            <span className="text-xs text-gray-500 hidden sm:inline font-medium">Vendor Portal</span>
          </div>
          <div className="flex items-center gap-1">
            {task && (
              <Button
                variant="ghost"
                size="sm"
                disabled={isDownloading}
                data-testid="button-download-task-record"
                onClick={async () => {
                  setIsDownloading(true);
                  try {
                    const url = showAll
                      ? `/api/vendor-portal/tasks/${task.id}/export?showAll=true`
                      : `/api/vendor-portal/tasks/${task.id}/export`;
                    const res = await apiRequest("GET", url);
                    const disposition = res.headers.get("Content-Disposition") ?? "";
                    const fnMatch = disposition.match(/filename="([^"]+)"/);
                    const filename = fnMatch ? fnMatch[1] : `task-${task.id}.pdf`;
                    const blob = await res.blob();
                    const objectUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = objectUrl;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(objectUrl); }, 1000);
                  } catch (err) {
                    toast({ title: "Download failed", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
                  } finally {
                    setIsDownloading(false);
                  }
                }}
              >
                {isDownloading
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Exporting…</>
                  : <><Download className="h-3.5 w-3.5 mr-1" />Download</>}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="button-logout">Sign Out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 flex-1 flex flex-col w-full">
        {task ? (
          <>
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-gray-900" data-testid="text-task-title">{task.title}</h2>
                      {task.isSample && (
                        <Badge
                          variant="secondary"
                          className="bg-purple-100 text-purple-800 hover:bg-purple-100"
                          data-testid="badge-sample-task-detail"
                        >
                          Sample
                        </Badge>
                      )}
                    </div>
                    {task.isSample ? (
                      <p className="text-xs text-gray-500 mt-1" data-testid="text-example-property-detail">
                        Example Property · 123 Demo Lane
                      </p>
                    ) : (task.propertyAddress || task.propertyName) ? (
                      <p className="text-xs text-gray-500 mt-1" data-testid="text-task-property-address">
                        {task.propertyAddress || task.propertyName}
                      </p>
                    ) : null}
                    {task.description && <p className="text-sm text-gray-600 mt-1">{task.description}</p>}
                    {task.dueDate && <p className="text-xs text-gray-400 mt-1" data-testid="text-task-due-date">Due: {formatDate(task.dueDate)}</p>}
                    <p className="text-xs text-gray-500 mt-1" data-testid="text-task-assignee">
                      Assigned to: {task.assignedTeamMemberName || "Owner"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-testid="text-task-priority">
                      Priority: {priorityWithSla(task.slaConfig, task.priority)}
                    </p>
                  </div>
                  <div className="ml-3 text-right">
                    <Badge className={statusColors[task.status] || "bg-gray-100 text-gray-800"}>
                      {task.status.replace("_", " ")}
                    </Badge>
                    {task.priceCents && <p className="text-sm font-medium mt-1">{formatCents(task.priceCents)}</p>}
                  </div>
                </div>
                {!isComplete && canWrite && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {task.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAcceptDialogOpen(true)}
                        data-testid="button-accept-task"
                      >
                        Accept Task
                      </Button>
                    )}
                    {/* Task #1968/#4315 — maintenance tasks land in `open` or
                        `sent_to_vendor` from the host queue; surface Accept,
                        Submit Quote, and Decline actions. Hide Accept/Submit
                        Quote when an owner-approval row already exists. */}
                    {task.type === "maintenance" && ["open", "sent_to_vendor"].includes(task.status) && (() => {
                      const approvalStatus = task.ownerApprovalStatus;
                      const approvalNote = task.ownerApprovalNote;
                      const hasApproval = !!approvalStatus;
                      const isPending = approvalStatus === "pending";
                      const isRejected = approvalStatus === "rejected";
                      return (
                        <>
                          {isPending && (
                            <div
                              className="w-full rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex items-center gap-2"
                              data-testid="banner-quote-pending-approval"
                            >
                              <span className="shrink-0">⏳</span>
                              <span>Quote submitted — waiting for owner approval.</span>
                            </div>
                          )}
                          {isRejected && (
                            <div
                              className="w-full rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
                              data-testid="banner-quote-rejected"
                            >
                              <div className="font-medium">Owner rejected your quote.</div>
                              {approvalNote && <div className="mt-0.5 text-xs text-red-700">{approvalNote}</div>}
                            </div>
                          )}
                          {!hasApproval && (
                            <Button
                              size="sm"
                              onClick={() => setAcceptDialogOpen(true)}
                              data-testid="button-accept-maintenance"
                            >
                              Accept Job
                            </Button>
                          )}
                          {!hasApproval && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSubmitQuoteOpen(true)}
                              data-testid="button-submit-quote"
                            >
                              Submit Quote
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            disabled={declineWithReasonMutation.isPending}
                            onClick={() => setDeclineWithReasonOpen(true)}
                            data-testid="button-decline-maintenance-with-reason"
                          >
                            Decline
                          </Button>
                        </>
                      );
                    })()}
                    {task.status === "accepted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate("in_progress")}
                        data-testid="button-start-work"
                      >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        {statusMutation.isPending ? "Starting..." : "Start Work"}
                      </Button>
                    )}
                    {["in_progress", "accepted"].includes(task.status) && (
                      <Button
                        size="sm"
                        disabled={completeMutation.isPending}
                        onClick={() => setCompleteConfirmOpen(true)}
                        data-testid="button-complete-task"
                      >
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                        Mark Complete
                      </Button>
                    )}
                    {/* Maintenance-only: on-hold + decline transitions. */}
                    {task.type === "maintenance" && task.status === "on_hold" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          disabled={statusMutation.isPending}
                          onClick={() => statusMutation.mutate("in_progress")}
                          data-testid="button-resume-maintenance"
                        >
                          <Play className="h-3.5 w-3.5 mr-1" />
                          {statusMutation.isPending ? "Resuming..." : "Resume Progress"}
                        </Button>
                        <div className="flex-1" />
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          disabled={statusMutation.isPending}
                          onClick={() => setDeclineOpen(true)}
                          data-testid="button-decline-maintenance"
                        >
                          Decline Job
                        </Button>
                      </>
                    )}
                    {task.type === "maintenance" && task.status !== "on_hold" && task.status !== "open" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate("on_hold")}
                        data-testid="button-hold-maintenance"
                      >
                        Put on hold
                      </Button>
                    )}
                    {task.type === "maintenance" && task.status !== "open" && task.status !== "on_hold" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={statusMutation.isPending}
                        onClick={() => setDeclineOpen(true)}
                        data-testid="button-decline-maintenance"
                      >
                        Decline
                      </Button>
                    )}
                  </div>
                )}
                {/* Owner: reassign to team member */}
                {isOwner && activeMembers.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-gray-500 mb-2">Assign to team member</p>
                    <div className="flex gap-2">
                      <Select value={reassignMemberId} onValueChange={setReassignMemberId}>
                        <SelectTrigger className="flex-1 h-8 text-sm" data-testid="select-reassign-member">
                          <SelectValue placeholder="Select member..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned (owner)</SelectItem>
                          {activeMembers.map(m => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {m.firstName} {m.lastName} {m.role === "owner" ? "(Owner)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reassignMutation.isPending}
                        onClick={() => reassignMutation.mutate()}
                        data-testid="button-reassign-task"
                      >
                        {reassignMutation.isPending ? "Saving..." : "Assign"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task #2187 — Estimate / locked rate panel (shown once vendor has accepted) */}
            {task.lockedRateType && task.lockedRateCents !== null && (
              <Card className="mb-4" data-testid="card-estimate-panel">
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Estimate</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rate</span>
                      <span className="font-medium">
                        {task.lockedRateType === "hourly"
                          ? `$${(task.lockedRateCents / 100).toFixed(2)}/hr`
                          : `$${(task.lockedRateCents / 100).toFixed(2)} flat`}
                      </span>
                    </div>
                    {task.lockedRateType === "hourly" && task.estimateHours && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Est. hours</span>
                        <span className="font-medium">{task.estimateHours}</span>
                      </div>
                    )}
                    {task.estimateCents !== null && task.estimateCents !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Labor estimate</span>
                        <span className="font-medium">${(task.estimateCents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {task.travelFeeCents !== null && task.travelFeeCents !== undefined && task.travelFeeCents > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Travel fee</span>
                        <span className="font-medium">${(task.travelFeeCents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {task.vendorEstimateNote && (
                      <p className="text-xs text-gray-500 mt-2 pt-2 border-t">{task.vendorEstimateNote}</p>
                    )}
                    {isComplete && task.priceCents !== null && task.priceCents !== undefined && (
                      <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                        <span>Final amount</span>
                        <span>${(task.priceCents / 100).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Task #2187 — Timer section (all accepted in-progress tasks).
                Hidden when the task's time-tracking toggle is off (e.g.
                acknowledgment-style form-tasks); the server rejects timer
                writes for those too. */}
            {task.lockedRateType && !isComplete && task.timeTrackingEnabled && (
              <div className="mb-4">
                <TimerSection
                  task={task}
                  vendorId={vendorId}
                  session={session}
                  queryClient={queryClient}
                  toast={toast}
                  canWrite={canWrite}
                />
              </div>
            )}

            {/* Forms-on-tasks — the form-task's fillable form / submission summary */}
            {task.formTemplateId != null && (
              <div className="mb-4">
                <TaskFormSection
                  task={task}
                  vendorId={vendorId}
                  queryClient={queryClient}
                  canWrite={canWrite}
                />
              </div>
            )}

            {(statusHistory.length > 0 || statusHistoryLoading) && (
              <Card className="mb-4" data-testid="section-status-history">
                <CardContent className="p-4">
                  <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-brand" />
                    Status history
                  </h3>
                  {statusHistoryLoading ? (
                    <div className="text-xs text-gray-400 py-2">Loading…</div>
                  ) : (
                    <ol className="relative border-l border-gray-200 ml-2 space-y-3" data-testid="list-status-history">
                      {statusHistory.map((entry, i) => {
                        const fromLabel = entry.fromStatus ? entry.fromStatus.replace(/_/g, " ") : "new task";
                        const toLabel = entry.toStatus.replace(/_/g, " ");
                        const actorTypeLabel = entry.actorType === "host" ? "Host" : entry.actorType === "vendor" ? "Vendor" : "System";
                        const actor = entry.changedByName ? `${entry.changedByName} (${actorTypeLabel})` : actorTypeLabel;
                        const actorBadge = entry.actorType === "host"
                          ? <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-brand-muted text-brand" data-testid={`badge-actor-type-${i}`}>Host</span>
                          : entry.actorType === "vendor"
                          ? <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700" data-testid={`badge-actor-type-${i}`}>Vendor</span>
                          : <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500" data-testid={`badge-actor-type-${i}`}>System</span>;
                        return (
                          <li key={i} className="ml-4" data-testid={`status-history-entry-${i}`}>
                            <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-white bg-gray-300" />
                            <p className="text-xs font-medium text-gray-800 capitalize">
                              {fromLabel} → {toLabel}
                            </p>
                            <p className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap">
                              {formatDateTime(entry.changedAt)} · {actor} {actorBadge}
                            </p>
                            {entry.note && (
                              <p className="text-[11px] text-gray-400 italic mt-0.5">{entry.note}</p>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="flex-1 flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />Messages
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-4 pt-0">
                <div className="flex-1 overflow-y-auto space-y-3 max-h-80 mb-4">
                  {messagesLoading ? (
                    <p className="text-sm text-gray-400 text-center py-4">Loading messages...</p>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No messages yet. Start the conversation below.</p>
                  ) : (
                    messages.map((msg) => {
                      if (msg.senderType === "system") {
                        return (
                          <div key={msg.id} className="flex justify-center py-1" data-testid={`message-${msg.id}`}>
                            <p className="text-xs text-gray-400 italic px-3 py-1 bg-gray-50 rounded-full border border-gray-100" data-testid={`badge-system-message-${msg.id}`}>
                              {msg.body}
                            </p>
                          </div>
                        );
                      }
                      const isVendorMsg = msg.senderType !== "host";
                      const msgAttachments: string[] = Array.isArray(msg.attachments) ? (msg.attachments as string[]) : [];
                      const senderName = (msg as TaskMessage & { senderName?: string }).senderName
                        || (isVendorMsg ? (msg.senderType === "vendor_member" ? "Team Member" : "Vendor") : "Host");
                      return (
                        <div key={msg.id} className={`flex ${isVendorMsg ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isVendorMsg ? "bg-blue-600 text-white" : "bg-white border text-gray-900"}`}
                            data-testid={`message-${msg.id}`}>
                            <p className={`text-xs mb-1 font-medium ${isVendorMsg ? "text-blue-100" : "text-gray-500"}`} data-testid={`text-sender-${msg.id}`}>{senderName}</p>
                            <p className="text-sm">{msg.body}</p>
                            {msgAttachments.length > 0 && (
                              <MessageAttachments urls={msgAttachments} messageId={`task-${msg.id}`} tone={isVendorMsg ? "dark" : "light"} />
                            )}
                            <p className={`text-xs mt-1 ${isVendorMsg ? "text-blue-200" : "text-gray-400"}`}>
                              {msg.createdAt ? formatDateTime(msg.createdAt) : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {canWrite ? (
                  <div className="space-y-2">
                    <AttachmentComposer
                      attachments={pendingAttachments}
                      setAttachments={setPendingAttachments}
                      disabled={sendMutation.isPending}
                      testIdPrefix="vendor-task-attach"
                      uploadUrlEndpoint="/api/vendor-portal/tasks/upload-url"
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (messageText.trim() || buildReadyAttachmentUrls(pendingAttachments).length > 0) && !pendingAttachments.some(a => a.status === "uploading") && sendMutation.mutate()}
                        data-testid="input-message"
                      />
                      <Button
                        size="sm"
                        disabled={(!messageText.trim() && buildReadyAttachmentUrls(pendingAttachments).length === 0) || sendMutation.isPending || pendingAttachments.some(a => a.status === "uploading")}
                        onClick={() => sendMutation.mutate()}
                        data-testid="button-send-message"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-center text-gray-400 py-2" data-testid="text-readonly-reason">
                    {task?.assignedTeamMemberId
                      ? `This task is assigned to ${task.assignedTeamMemberName || "another team member"}. Ask the owner to reassign it if you need to respond.`
                      : "This task is not assigned to you. Ask the owner to reassign it if you need to respond."}
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400">Task not found</p>
            <Button variant="ghost" onClick={onBack} className="mt-4">Back to tasks</Button>
          </div>
        )}
      </div>
      {task && (
        <AcceptWithEstimateDialog
          open={acceptDialogOpen}
          onOpenChange={setAcceptDialogOpen}
          task={task}
          vendorId={vendorId}
          session={session}
          queryClient={queryClient}
          toast={toast}
        />
      )}
      {task && (
        <SubmitQuoteDialog
          open={submitQuoteOpen}
          onOpenChange={setSubmitQuoteOpen}
          task={task}
          vendorId={vendorId}
          session={session}
          queryClient={queryClient}
          toast={toast}
        />
      )}
      {/* Decline-with-reason dialog — for open/sent_to_vendor maintenance jobs */}
      <Dialog
        open={declineWithReasonOpen}
        onOpenChange={(open) => { if (!declineWithReasonMutation.isPending) { setDeclineWithReasonOpen(open); if (!open) setDeclineReason(""); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="dialog-decline-with-reason">
          <DialogHeader>
            <DialogTitle>Decline this job</DialogTitle>
            <DialogDescription>
              Your reason will be sent to the host as a message. The job will return to the host's queue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="textarea-decline-reason" className="text-sm font-medium">Reason for declining</Label>
            <Textarea
              id="textarea-decline-reason"
              placeholder="e.g. Outside our service area, unavailable during this timeframe..."
              rows={3}
              maxLength={2000}
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              disabled={declineWithReasonMutation.isPending}
              data-testid="textarea-decline-reason"
            />
            <p className="text-xs text-gray-400">{declineReason.length}/2000</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => { setDeclineWithReasonOpen(false); setDeclineReason(""); }}
              disabled={declineWithReasonMutation.isPending}
              data-testid="button-cancel-decline-with-reason"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!declineReason.trim() || declineWithReasonMutation.isPending}
              onClick={() => declineWithReasonMutation.mutate()}
              data-testid="button-confirm-decline-with-reason"
            >
              {declineWithReasonMutation.isPending ? "Declining..." : "Decline Job"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <AlertDialogContent data-testid="dialog-decline-maintenance-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Decline this job?</AlertDialogTitle>
            <AlertDialogDescription>
              It will go back to the host's queue and the chat will close.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-decline-maintenance">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-decline-maintenance"
              disabled={statusMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                statusMutation.mutate("open", {
                  onSettled: () => setDeclineOpen(false),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {statusMutation.isPending ? "Declining..." : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={completeConfirmOpen}
        onOpenChange={(open) => { if (!completeMutation.isPending) setCompleteConfirmOpen(open); }}
      >
        <AlertDialogContent data-testid="dialog-complete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark task complete?</AlertDialogTitle>
            <AlertDialogDescription>
              Please confirm that your hours and pricing are finalized as you will not be able to edit it once the task is marked complete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completeMutation.isPending} data-testid="button-cancel-complete-confirm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={completeMutation.isPending}
              onClick={(e) => { e.preventDefault(); completeMutation.mutate(); }}
              data-testid="button-confirm-complete"
            >
              {completeMutation.isPending ? "Saving..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
